import {
  PLANNER_AUTOMATION_ACTIONS_REGISTRY,
} from '../modules/crm/automation-v2/components-v2/automation-v2/automation-v2.interface';
import {
  PLANNER_STATUS,
  PLANNER_STRUCTURE,
  PlannerActionsDropdownItem,
  PlannerActionType,
  PlannerItem,
} from '../components/layout/stacked-sidebar/components/planner-v2/planner-v2.interface';
import { Subject } from 'rxjs';
import { ActionListItem } from '../enum/constants';
import { PlannerActionService } from '../services/planner-action.service';
import {
  EDIT_OLX_CONTENT_ENABLED_ACTIONS,
  PLANNER_OLX_ACTIONS_REGISTRY,
} from '../modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';
import { AppTableHeader, ColumnSortType, TableColumnTypes } from '../model/table.model';


export const PLANNER_FILTERS = [
  {
    key: PLANNER_STATUS.ACTIVE,
    name: 'olx.planners.active',
    values: [PLANNER_STATUS.ACTIVE],
    color: 'green',
    icon: 'pending',
  },
  {
    key: PLANNER_STATUS.PROCESSING,
    name: 'olx.planners.processing',
    values: [PLANNER_STATUS.PROCESSING],
    color: 'royalblue',
    icon: 'autorenew',
  },
  {
    key: PLANNER_STATUS.COMPLETED,
    name: 'olx.planners.completed',
    values: [PLANNER_STATUS.COMPLETED],
    color: 'blue',
    icon: 'check_circle',
  },
  {
    key: PLANNER_STATUS.DISABLED,
    name: 'olx.planners.disabled',
    values: [PLANNER_STATUS.DISABLED],
    color: 'gray',
    icon: 'cancel',
  },
  {
    key: PLANNER_STATUS.FAILED,
    name: 'olx.planners.failed',
    values: [PLANNER_STATUS.FAILED],
    color: 'red',
    icon: 'error',
  },
  {
    key: PLANNER_STATUS.WARNING,
    name: 'olx.planners.warning',
    values: [PLANNER_STATUS.WARNING],
    color: 'orange',
    icon: 'warning',
  },
  {
    key: PLANNER_STATUS.DRAFT,
    name: 'olx.planners.draft',
    values: [PLANNER_STATUS.DRAFT],
    color: 'black',
    icon: 'draft',
  },
];

export interface PlannerActionContext {
  items: PlannerItem[];
  destroy$: Subject<void>;
  active?: boolean;
  status?: PLANNER_STATUS;
  errorMessageKey?: string;
  reloadTable?: () => void;
  clearSelection?: () => void;
}

export const PLANNER_ACTIONS_REGISTRY: Record<string, PlannerActionsDropdownItem> = {
  ...PLANNER_OLX_ACTIONS_REGISTRY,
  ...PLANNER_AUTOMATION_ACTIONS_REGISTRY,
};

export const ACTIVATE_PLANNER_NOT_SHOWN_STATUSES = [
  PLANNER_STATUS.ACTIVE,
  PLANNER_STATUS.PROCESSING,
  PLANNER_STATUS.COMPLETED,
];

export const DEACTIVATE_PLANNER_NOT_SHOWN_STATUSES = [
  PLANNER_STATUS.DISABLED,
  PLANNER_STATUS.PROCESSING,
  PLANNER_STATUS.DRAFT,
];

export const EDIT_CONTENT_ENABLED_ACTIONS: PlannerActionType[] = [
  ...EDIT_OLX_CONTENT_ENABLED_ACTIONS,
];

export const PLANNER_TABLE_ACTIONS_REGISTRY: PlannerActionListItem[] = [
  {
    label: 'run_now',
    method: 'runPlanners',
    icon: 'play_circle',
    color_icon: 'green',
    unseen: (items) =>
      (items).some(
        (item) => !item?.active || !item.next_trigger_at,
      ),
    bulk: true,
  },
  {
    label: 'edit2',
    method: 'updatePlanner',
    icon: 'edit',
    color_icon: 'gray',
    disabled: (items) =>
      (items).some(
        (item) => item.structure_type === PLANNER_STRUCTURE.GROUP,
      ),
  },
  {
    label: 'edit_content',
    method: 'updatePlannerContent',
    icon: 'article',
    color_icon: 'gray',
    disabled: (items) =>
      (items).some(
        (item) => !EDIT_CONTENT_ENABLED_ACTIONS.includes(item?.action)
          || item.structure_type === PLANNER_STRUCTURE.GROUP,
      ),
  },
  {
    label: 'activate',
    method: 'activatePlanners',
    icon: 'toggle_on',
    color_icon: 'green',
    unseen: (items) =>
      (items).some(
        (item) => ACTIVATE_PLANNER_NOT_SHOWN_STATUSES.includes(item?.status),
      ),
    bulk: true,
  },
  {
    label: 'deactivate',
    method: 'deactivatePlanners',
    icon: 'toggle_off',
    color_icon: 'red',
    unseen: (items) =>
      (items).some(
        (item) => DEACTIVATE_PLANNER_NOT_SHOWN_STATUSES.includes(item?.status),
      ),
    bulk: true,
  },
  {
    label: 'cancel',
    method: 'cancelPlanners',
    icon: 'cancel',
    color_icon: 'red',
    unseen: (items) =>
      (items).some(
        (item) => !item?.active,
      ),
    bulk: true,
  },
  {
    label: 'archive',
    method: 'archivePlanners',
    icon: 'archive',
    color_icon: 'red',
    unseen: (items) =>
      (items).some(
        (item) => !item?.active,
      ),
    bulk: true,
  },
  {
    label: 'unarchive',
    method: 'unarchivePlanners',
    icon: 'archive',
    color_icon: 'green',
    unseen: (items) =>
      (items).some(
        (item) => item?.active,
      ),
    bulk: true,
  },
];


export const plannerExtraActionContextMap: Partial<Record<
  keyof PlannerActionService,
  Partial<PlannerActionContext>
>> = {
  activatePlanners: {
    status: PLANNER_STATUS.ACTIVE,
    errorMessageKey: 'notifications_texts.error.failed_to_activate_planner',
  },
  deactivatePlanners: {
    status: PLANNER_STATUS.DISABLED,
    errorMessageKey: 'notifications_texts.error.failed_to_deactivate_planner',
  },
  archivePlanners: {
    active: false,
    errorMessageKey: 'notifications_texts.error.failed_to_archive_planner',
  },
  unarchivePlanners: {
    active: true,
    errorMessageKey: 'notifications_texts.error.failed_to_unarchive_planner',
  },
  runPlanners: {
    errorMessageKey: 'notifications_texts.error.failed_to_run_planner',
  },
  cancelPlanners: {
    errorMessageKey: 'notifications_texts.error.failed_to_cancel_planner',
  },
};


export interface PlannerActionListItem extends ActionListItem<PlannerActionService, PlannerItem> {
}

export const PLANNER_TABLE_HEADERS: AppTableHeader[] = [
  {
    value: 'primary',
    sort: ColumnSortType.NONE,
    name: '',
    type: TableColumnTypes.CHECKBOX,
    className: 'table-checkbox'
  },
  {
    value: 'title',
    sort: ColumnSortType.NONE,
    name: 'olx.planners.planner_name',
    type: TableColumnTypes.TITLE,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'status',
    sort: ColumnSortType.NONE,
    name: 'status',
    type: TableColumnTypes.PLANNER_V2_STATUS,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'planner_action',
    sort: ColumnSortType.NONE,
    name: 'planner.action',
    type: TableColumnTypes.TEXT_TRANSLATE,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'created_at',
    sort: ColumnSortType.NONE,
    name: 'created',
    type: TableColumnTypes.DATE,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'next_trigger_at',
    sort: ColumnSortType.NONE,
    name: 'next_trigger_at',
    type: TableColumnTypes.DATE,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'last_triggered_at',
    sort: ColumnSortType.NONE,
    name: 'last_triggered_at',
    type: TableColumnTypes.DATE,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'occurrences_count',
    sort: ColumnSortType.NONE,
    name: 'occurrences_count',
    type: TableColumnTypes.TEXT,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'error_code',
    sort: ColumnSortType.NONE,
    name: 'additional_information',
    type: TableColumnTypes.ERROR_CODE,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'action',
    sort: ColumnSortType.NONE,
    name: '',
    type: TableColumnTypes.ACTION,
    className: 'three-dot',
    translate: true,
  },
];
